import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlacementWithLogical } from '@chakra-ui/react';
import {
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';

import { useModules } from '@izimi/modules';

import { NavigationNotificationsIcon } from '../navigation';
import { NotificationPopOverBody } from './NotificationPopOverBody';

type Props<Routes extends Record<string, string>> = {
  routes: Routes;
};

export const NotificationsPopOver = <Routes extends Record<string, string>>({
  routes,
}: Props<Routes>) => {
  const { t } = useTranslation('shared');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const popoverContentRef = useRef<HTMLDivElement | null>(null);

  const {
    notifications: { usePagedNotifications, useMarkNotificationsAsViewed },
  } = useModules();
  const { notifications } = usePagedNotifications({
    pageSize: 10,
  });
  const { markNotificationsAsViewed } = useMarkNotificationsAsViewed();

  const popoverPlacement = useBreakpointValue<PlacementWithLogical>(
    {
      base: 'bottom',
      md: 'right-end',
    },
    { ssr: false },
  );

  useEffect(() => {
    const notViewedNotifications = notifications?.filter((n) => !n.viewed).map((n) => n.id);

    if (isOpen && notViewedNotifications?.length) {
      markNotificationsAsViewed({ ids: notViewedNotifications });
    }
  }, [notifications, isOpen, markNotificationsAsViewed]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverContentRef.current && !popoverContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    // extra flex wrapper is needed: https://github.com/chakra-ui/chakra-ui/issues/3173
    <Flex flex={1} justifyContent='center'>
      <Popover
        placement={popoverPlacement}
        closeOnBlur={false}
        closeOnEsc
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        preventOverflow
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              padding: 30,
            },
          },
        ]}>
        <PopoverTrigger>
          <IconButton
            aria-label={t('navigation.notifications')}
            backgroundColor='transparent'
            variant='menuIcon'
            icon={<NavigationNotificationsIcon />}
            title={t('navigation.notifications')}
            data-testid='notifications-menu-button'
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            ref={popoverContentRef}
            rootProps={{
              zIndex: 9999,
            }}
            maxHeight={{ base: '75vh', md: '80vh' }}
            width={{ base: 'calc(100vw - 30px)', md: 'md' }}
            maxWidth='calc(100% - 30px)'
            boxShadow='0px 4px 8px rgba(61, 56, 54, 0.04), 0px 8px 16px rgba(61, 56, 54, 0.04), 0px 12px 32px rgba(61, 56, 54, 0.12)'>
            <NotificationPopOverBody<Routes> routes={routes} />
          </PopoverContent>
        </Portal>
      </Popover>
    </Flex>
  );
};
